// client/src/writer/hooks/useChapterContentLoader.ts
// Cache-first chapter content loader with duplicate call prevention

import { useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../../lib/queryClient';
import {
  getCachedChapterContent,
  setCachedChapterContent,
  updateCachedChapterVersionHistory
} from '../utils/chapterContentCache';

interface ChapterContentResult {
  content: string;
  versionHistory: Array<{
    content: string;
    generatedSummary: string;
    timestamp: number;
  }>;
  fromCache: boolean;
}

export const useChapterContentLoader = () => {
  const queryClient = useQueryClient();
  // Track active requests to prevent duplicates
  const activeRequests = useRef<Map<string, Promise<ChapterContentResult>>>(new Map());

  /**
   * Loads chapter content with cache-first strategy
   * 1. Check localStorage cache first
   * 2. If not in cache or stale, fetch from API
   * 3. Store result in cache
   * 4. Prevent duplicate concurrent requests
   */
  const loadChapterContent = useCallback(async (
    projectId: string, 
    chapterId: string,
    forceRefresh: boolean = false
  ): Promise<ChapterContentResult> => {
    
    const requestKey = `${projectId}:${chapterId}`;
    
    // Check if there's already an active request for this chapter
    if (activeRequests.current.has(requestKey) && !forceRefresh) {
      return activeRequests.current.get(requestKey)!;
    }

    // Create the request promise
    const requestPromise = (async (): Promise<ChapterContentResult> => {
      try {
        // Step 1: Check localStorage cache first (unless force refresh)
        if (!forceRefresh) {
          const cachedContent = getCachedChapterContent(projectId, chapterId);
          
          if (cachedContent) {
            
            // Check if cache is relatively fresh (less than 5 minutes old)
            const cacheAge = Date.now() - cachedContent.lastModified;
            const isCacheFresh = cacheAge < 5 * 60 * 1000; // 5 minutes
            
            if (isCacheFresh) {
              return {
                content: cachedContent.content,
                versionHistory: cachedContent.versionHistory,
                fromCache: true
              };
            }
            
          }
        }

        // Step 2: Fetch from API
        const response = await apiRequest(`/api/chapters/${chapterId}/content`, 'GET');
        
        if (!response.ok) {
          throw new Error(`API request failed: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Step 3: Process the response
        const content = data.content || '';
        const versionHistory = data.versionHistory || [];
        
        // Step 4: Update cache
        setCachedChapterContent(
          projectId,
          chapterId,
          content,
          versionHistory,
          data.status
        );
        
        return {
          content,
          versionHistory,
          fromCache: false
        };
        
      } catch (error) {
        console.error(`❌ Failed to load content for chapter ${chapterId}:`, error);
        
        // Fallback to cache even if stale
        const cachedContent = getCachedChapterContent(projectId, chapterId);
        if (cachedContent) {
          return {
            content: cachedContent.content,
            versionHistory: cachedContent.versionHistory,
            fromCache: true
          };
        }
        
        // Last resort - return empty content
        return {
          content: '',
          versionHistory: [],
          fromCache: false
        };
      } finally {
        // Clean up the active request
        activeRequests.current.delete(requestKey);
      }
    })();

    // Store the promise to prevent duplicates
    activeRequests.current.set(requestKey, requestPromise);
    
    return requestPromise;
  }, []);

  /**
   * Saves chapter content to API and updates cache
   */
  const saveChapterContent = useCallback(async (
    projectId: string,
    chapterId: string,
    content: string,
    generatedSummary?: string,
    revisionNotes?: string
  ): Promise<boolean> => {
    try {
      
      const payload: any = {
        fullTextContent: content,
        generatedSummary: generatedSummary || null
      };
      
      if (revisionNotes) {
        payload.revisionNotes = revisionNotes;
      }
      
      await apiRequest(`/api/chapters/${chapterId}/content`, 'POST', payload);
      
      // Create new version entry
      const newVersion = {
        content,
        generatedSummary: generatedSummary || 'Content saved',
        timestamp: Date.now()
      };
      
      // Update cache with new version
      updateCachedChapterVersionHistory(projectId, chapterId, newVersion);

      // 🔧 CRITICAL FIX: Trigger progress recompute after full-text save
      try {
        // Use canonical project ID for all cache invalidations
// FIX: Invalidate queries with correct filter object shape.
        queryClient.invalidateQueries({queryKey: ['story-project-summary', projectId]});
// FIX: Invalidate queries with correct filter object shape.
        queryClient.invalidateQueries({queryKey: ['project-progress', projectId]});
// FIX: Invalidate queries with correct filter object shape.
        queryClient.invalidateQueries({queryKey: ['/api/story-projects/summaries']});

        // 🛡️ SMART INVALIDATION: Only invalidate chapters cache if it doesn't have generated content
        const currentChaptersCache = queryClient.getQueryData(['project-chapters', projectId]);
        const cacheHasContent = currentChaptersCache && Array.isArray(currentChaptersCache) &&
          currentChaptersCache.some((ch: any) => {
            const hasPreview = ch.previewContent && ch.previewContent.length > 0;
            const hasFullText = ch.contentCache?.hasFullText === true ||
              (ch.contentCache?.fullTextWordCount && ch.contentCache.fullTextWordCount > 0);
            return hasPreview || hasFullText;
          });

        if (cacheHasContent) {
        } else {
// FIX: Invalidate queries with correct filter object shape.
          queryClient.invalidateQueries({queryKey: ['project-chapters', projectId]});
        }

        // Force immediate refetch of progress to get 100% chapters progress
// FIX: Invalidate queries with correct filter object shape.
        await queryClient.refetchQueries({queryKey: ['project-progress', projectId]});

      } catch (progressError) {
        console.error('❌ Failed to trigger progress update after full-text save:', progressError);
      }

      return true;
      
    } catch (error) {
      console.error(`❌ Failed to save content for chapter ${chapterId}:`, error);
      return false;
    }
  }, []);

  /**
   * Clears active requests (useful for cleanup)
   */
  const clearActiveRequests = useCallback(() => {
    activeRequests.current.clear();
  }, []);

  return {
    loadChapterContent,
    saveChapterContent,
    clearActiveRequests
  };
};